// Zakázky: seznam pro kanban a seznam, filtr Ztracené, optimistická změna fáze a cache detailů.
import { defineStore } from 'pinia'
import { useClientsStore } from './clients'
import { computed, ref } from 'vue'
import {
  JOB_STAGES,
  type AiNoteResponse,
  type CreateJobInput,
  type JobDetail,
  type JobStage,
  type JobSummary,
  type FirstResponseInput,
  type LostJobInput,
  type UpdateJobInput,
} from '@wac-buddy/shared'
import {
  changeJobStage,
  createJob,
  getJob,
  listJobs,
  markJobLost,
  postFirstResponse,
  postJobNote,
  restoreJob,
  updateJob,
} from '@/api/jobs'

export type ListStatus = 'idle' | 'loading' | 'ready' | 'error'
export type JobsView = 'kanban' | 'list'

const VIEW_KEY = 'wac-buddy.jobs.view'
const PAGE_SIZE = 200
const MAX_PAGES = 10

function readView(): JobsView {
  try {
    return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'kanban'
  } catch {
    return 'kanban'
  }
}

function toSummary(j: JobSummary): JobSummary {
  return {
    id: j.id,
    title: j.title,
    stage: j.stage,
    clientId: j.clientId,
    clientName: j.clientName,
    siteAddress: j.siteAddress,
    nextDate: j.nextDate,
    hasPendingProposal: j.hasPendingProposal,
    source: j.source,
    estimatedAmountMinor: j.estimatedAmountMinor,
    lostAt: j.lostAt,
    updatedAt: j.updatedAt,
  }
}

const byUpdatedDesc = (a: JobSummary, b: JobSummary) => b.updatedAt.localeCompare(a.updatedAt)

export const useJobsStore = defineStore('jobs', () => {
  const items = ref<JobSummary[]>([])
  const lostItems = ref<JobSummary[]>([])
  const status = ref<ListStatus>('idle')
  const showLost = ref(false)
  const view = ref<JobsView>(readView())
  const details = ref<Record<string, JobDetail>>({})

  let requestSeq = 0

  /** Aktivní zakázky (kanban a seznam), nebo ztracené podle `showLost`. Prochází všechny stránky. */
  async function load(): Promise<void> {
    const seq = ++requestSeq
    const lost = showLost.value
    status.value = 'loading'
    try {
      const all: JobSummary[] = []
      let cursor: string | null = null
      for (let page = 0; page < MAX_PAGES; page++) {
        const res = await listJobs({ lost, cursor, limit: PAGE_SIZE })
        all.push(...res.data)
        cursor = res.nextCursor
        if (!cursor) break
      }
      if (seq !== requestSeq) return
      if (lost) lostItems.value = all
      else items.value = all
      status.value = 'ready'
    } catch {
      if (seq !== requestSeq) return
      status.value = 'error'
    }
  }

  async function setShowLost(next: boolean): Promise<void> {
    showLost.value = next
    await load()
  }

  function setView(next: JobsView): void {
    view.value = next
    try {
      localStorage.setItem(VIEW_KEY, next)
    } catch {
      /* soukromý režim bez úložiště */
    }
  }

  /** Zakázky ve fázi, nejnověji změněné nahoře. */
  const byStage = computed(() => {
    const groups = Object.fromEntries(JOB_STAGES.map((s) => [s, [] as JobSummary[]])) as Record<
      JobStage,
      JobSummary[]
    >
    for (const job of items.value) groups[job.stage]?.push(job)
    for (const stage of JOB_STAGES) groups[stage].sort(byUpdatedDesc)
    return groups
  })

  /** Jednoduchý seznam podle poslední aktualizace. */
  const sorted = computed(() =>
    [...(showLost.value ? lostItems.value : items.value)].sort(byUpdatedDesc),
  )

  function patchLocal(id: string, patch: Partial<JobSummary>): JobSummary | undefined {
    const i = items.value.findIndex((j) => j.id === id)
    if (i < 0) return undefined
    const prev = items.value[i]!
    items.value[i] = { ...prev, ...patch }
    return prev
  }

  /**
   * Změní fázi optimisticky: karta se přesune hned, při chybě API se vrátí zpět a chyba se předá dál.
   * `marketingConsent` se posílá jen při přechodu do Hotovo.
   */
  /** Změna zakázky mění i detail klienta (zakázky, časová osa, počty): jeho cache se zahodí. */
  function forgetClient(clientId: string | undefined): void {
    if (clientId) delete useClientsStore().details[clientId]
  }

  async function moveStage(
    id: string,
    stage: JobStage,
    options: { marketingConsent?: boolean } = {},
  ): Promise<void> {
    const current = items.value.find((j) => j.id === id) ?? details.value[id]
    if (current && current.stage === stage) return
    const previousStage = current?.stage
    const cached = details.value[id]
    patchLocal(id, { stage })
    if (cached) details.value[id] = { ...cached, stage }
    try {
      const detail = await changeJobStage(id, {
        stage,
        ...(options.marketingConsent !== undefined
          ? { marketingConsent: options.marketingConsent }
          : {}),
      })
      details.value[id] = detail
      patchLocal(id, toSummary(detail))
      forgetClient(detail.clientId)
    } catch (error) {
      if (previousStage) {
        patchLocal(id, { stage: previousStage })
        if (cached) details.value[id] = cached
      }
      throw error
    }
  }

  /** Označí zakázku jako ztracenou: zmizí z kanbanu. */
  async function markLost(id: string, input: LostJobInput): Promise<JobDetail> {
    const detail = await markJobLost(id, input)
    details.value[id] = detail
    items.value = items.value.filter((j) => j.id !== id)
    forgetClient(detail.clientId)
    return detail
  }

  /**
   * První reakce na poptávku. Zavolání a návrh termínu zakázku nechají v kanbanu,
   * odmítnutí ji ztratí (zmizí z kanbanu stejně jako po ručním označení).
   */
  async function firstResponse(id: string, kind: FirstResponseInput['kind']): Promise<JobDetail> {
    const detail = await postFirstResponse(id, { kind })
    details.value[id] = detail
    if (detail.lostAt) items.value = items.value.filter((j) => j.id !== id)
    else patchLocal(id, toSummary(detail))
    forgetClient(detail.clientId)
    return detail
  }

  /** Vrátí ztracenou zakázku do kanbanu. */
  async function restore(id: string): Promise<JobDetail> {
    const detail = await restoreJob(id)
    details.value[id] = detail
    lostItems.value = lostItems.value.filter((j) => j.id !== id)
    items.value = [toSummary(detail), ...items.value.filter((j) => j.id !== id)]
    forgetClient(detail.clientId)
    return detail
  }

  async function create(input: CreateJobInput): Promise<JobDetail> {
    const detail = await createJob(input)
    details.value[detail.id] = detail
    items.value = [toSummary(detail), ...items.value.filter((j) => j.id !== detail.id)]
    forgetClient(detail.clientId)
    return detail
  }

  async function update(id: string, input: UpdateJobInput): Promise<JobDetail> {
    const previousClientId = details.value[id]?.clientId
    const detail = await updateJob(id, input)
    details.value[id] = detail
    patchLocal(id, toSummary(detail))
    forgetClient(detail.clientId)
    if (previousClientId !== detail.clientId) forgetClient(previousClientId)
    return detail
  }

  /**
   * Poznámka k zakázce (hlídač slibů): uloží se do časové osy zakázky i klienta a Buddy v ní hledá
   * sliby a úkoly. Chyba (429 při vyčerpaném limitu) jde dál. Po uložení se detail načte znovu;
   * selhání načtení uloženou poznámku nezneplatní.
   */
  async function addNote(id: string, text: string): Promise<AiNoteResponse> {
    const res = await postJobNote(id, { text })
    forgetClient(details.value[id]?.clientId)
    try {
      await fetchDetail(id, true)
    } catch {
      /* poznámka je uložená, časová osa se obnoví při dalším načtení detailu */
    }
    return res
  }

  async function fetchDetail(id: string, force = false): Promise<JobDetail> {
    const cached = details.value[id]
    if (cached && !force) return cached
    const detail = await getJob(id)
    details.value[id] = detail
    return detail
  }

  function applyDetail(detail: JobDetail): void {
    details.value[detail.id] = detail
    patchLocal(detail.id, toSummary(detail))
  }

  function invalidate(): void {
    details.value = {}
    items.value = []
    lostItems.value = []
    status.value = 'idle'
  }

  return {
    items,
    lostItems,
    status,
    showLost,
    view,
    details,
    byStage,
    sorted,
    load,
    setShowLost,
    setView,
    moveStage,
    markLost,
    firstResponse,
    restore,
    create,
    update,
    addNote,
    fetchDetail,
    applyDetail,
    invalidate,
  }
})
